import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../lib/api'

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/manual')({
  component: ManualPage,
})

/* ───── Types ───── */

interface ManualSection {
  id: string
  title: string
  content: string
}

/* ───── Documentation Content ───── */

const MANUAL_SECTIONS: ManualSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: `Welcome to IRIS -- the Intelligence Research and Investigation System. This manual provides an overview of the platform's key features and how to use them effectively.

After logging in, you will be directed to the Dashboard, which provides a high-level overview of system activity including entity counts, recent messages, active groups, and a geographic heatmap.

Use the sidebar navigation on the left to access different sections of the application. The sidebar can be collapsed by clicking the toggle at the bottom.`,
  },
  {
    id: 'search',
    title: 'Search',
    content: `The Search feature provides unified full-text search across entities, groups, and messages.

How to search:
- Type your query in the search bar at the top of the Search page
- Results are organized into tabs: Entities, Groups, and Messages
- Use filters to narrow results by platform, date range, or risk level
- Click any result to navigate to its detail page

Search supports partial matching and is powered by Typesense for fast, typo-tolerant results. Use the autocomplete suggestions that appear as you type to quickly find what you are looking for.`,
  },
  {
    id: 'entities',
    title: 'Entities',
    content: `Entities represent individual persons of interest tracked by the system. Each entity has a unique profile aggregated from multiple messaging platforms.

Entity Detail Page tabs:
- App Data: Classification tags, risk breakdown, and raw metadata
- Personal Details: Name, bio, and profile information from each platform source
- Accounts / Profiler: Cross-platform linked accounts, phone numbers, social media, and CallerAPI results
- Map: Known locations plotted on an interactive map
- Groups: Group memberships with activity metrics
- Messages: Message history with date and group filters
- Media: Images, videos, and documents associated with the entity
- Network: Graph visualization showing the entity's connections

Use the Enrich button to trigger additional data gathering from external sources like CallerAPI.`,
  },
  {
    id: 'profiler',
    title: 'OSINT Profiler',
    content: `The OSINT Profiler (accessible via /profiler/{id}) provides a dedicated view for intelligence profiling of an entity.

The profiler displays:
- Entity avatar and basic identification information on the left panel
- Social media accounts across all tracked platforms
- Phone numbers and email addresses
- Linked identities with confidence scores
- Cross-platform account correlation
- AI-generated profile summary

The profiler aggregates data from the entity's profile endpoint, combining enrichment results, platform data, and analysis into a single actionable view.`,
  },
  {
    id: 'groups',
    title: 'Groups',
    content: `Groups represent messaging channels, chat rooms, or group conversations from various platforms.

Group Detail Page tabs:
- App Data: Group metadata, classification, and risk assessment
- Messages: Searchable message history with date range filters
- Members: Group member list with role and risk indicators
- Media: Shared media files within the group
- AI Summary: AI-generated summary of group activity and key topics
- OCR Results: Text extracted from images shared in the group

You can sort members by name, role, risk score, or last activity. Use the risk level filter to focus on high-risk members.`,
  },
  {
    id: 'investigation',
    title: 'Investigation Board',
    content: `The Investigation Board is an interactive graph-based workspace for building and exploring connection networks.

Key features:
- Create investigation sessions to save and organize your work
- Add entities to the canvas and expand their connections
- Find shortest paths between two entities
- Filter by relationship type and adjust expansion depth
- Export investigation graphs for reporting

Use the search bar within the investigation board to find and add entities. Right-click nodes for context actions like expand, remove, or view details.`,
  },
  {
    id: 'alerts',
    title: 'Alerts',
    content: `The Alerts system allows you to set up automated monitoring rules that trigger notifications when conditions are met.

Creating alert rules:
- Navigate to Alerts from the sidebar
- Click "New Rule" to define a new alert
- Choose the alert type: keyword match, new entity, risk threshold, or group activity
- Configure conditions and thresholds
- Enable or disable rules as needed

Alert triggers appear in the notification bell in the top navigation bar. You can view alert history and manage rule configurations from the Alerts section.`,
  },
  {
    id: 'admin',
    title: 'Administration',
    content: `Admin features are available to users with the admin or collection_manager role.

Available admin tools:
- Users: Create, edit, and manage user accounts and roles
- Audit Log: Review all system actions with user, action, and timestamp filters
- Collection: Manage collection avatars, assignments, and platform configurations
- Settings: Configure system-wide settings
- System Health: Monitor service status and performance metrics
- Maintenance: Trigger search reindex, graph rebuild, cache clear, and view refresh
- API Keys: Manage external API access keys
- Compliance: Data export and deletion requests for regulatory compliance

Role hierarchy: viewer < analyst < collection_manager < admin`,
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    content: `IRIS supports the following keyboard shortcuts for faster navigation:

Navigation:
- Ctrl/Cmd + K: Open global search
- Escape: Close modals and drawers

Investigation Board:
- Delete/Backspace: Remove selected nodes
- Ctrl/Cmd + A: Select all nodes
- Ctrl/Cmd + Z: Undo last action
- Scroll wheel: Zoom in/out
- Click + drag: Pan the canvas
- Shift + click: Multi-select nodes`,
  },
]

/* ───── Sub-components ───── */

function SideNav({
  sections,
  activeId,
  onSelect,
}: {
  sections: ManualSection[]
  activeId: string
  onSelect: (id: string) => void
}) {
  return (
    <nav className="space-y-0.5" data-testid="manual-sidenav">
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() => onSelect(section.id)}
          className={`
            w-full text-left px-3 py-2 rounded-[var(--radius-md)] text-sm transition-colors
            ${
              activeId === section.id
                ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]'
            }
          `}
        >
          {section.title}
        </button>
      ))}
    </nav>
  )
}

/* ───── Search Highlight ───── */

function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-[var(--color-warning)]/30 text-[var(--color-text-primary)] rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

/* ───── Content Renderer ───── */

function SectionContent({ content, searchQuery }: { content: string; searchQuery: string }) {
  return (
    <div className="prose-sm">
      {content.split('\n\n').map((paragraph, i) => {
        const lines = paragraph.split('\n')
        const isListBlock = lines.every((l) => l.startsWith('- ') || l.trim() === '')

        if (isListBlock) {
          return (
            <ul key={i} className="mb-4 space-y-1.5 pl-1">
              {lines
                .filter((l) => l.startsWith('- '))
                .map((line, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-[var(--color-text-primary)]">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--color-accent)] shrink-0" />
                    <span><HighlightText text={line.slice(2)} query={searchQuery} /></span>
                  </li>
                ))}
            </ul>
          )
        }

        if (lines.length > 1 && lines[0]!.endsWith(':')) {
          return (
            <div key={i} className="mb-4">
              <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-2">
                <HighlightText text={lines[0]!} query={searchQuery} />
              </h3>
              <ul className="space-y-1.5 pl-1">
                {lines.slice(1).filter((l) => l.startsWith('- ')).map((line, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-[var(--color-text-primary)]">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--color-accent)] shrink-0" />
                    <span><HighlightText text={line.slice(2)} query={searchQuery} /></span>
                  </li>
                ))}
              </ul>
            </div>
          )
        }

        return (
          <p key={i} className="mb-4 text-sm leading-relaxed text-[var(--color-text-primary)]">
            <HighlightText text={paragraph} query={searchQuery} />
          </p>
        )
      })}
    </div>
  )
}

/* ───── File Upload & OCR ───── */

interface UploadedFile {
  name: string
  size: number
  type: string
  previewUrl?: string
}

function DocumentUploadPanel() {
  const [isDragging, setIsDragging] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [ocrStatus, setOcrStatus] = useState<Record<string, 'idle' | 'processing' | 'done' | 'error'>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      // Use fetch directly for multipart/form-data
      const response = await fetch('/api/v1/manual/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (!response.ok) throw new Error('Upload failed')
      return response.json() as Promise<{ file_id: string; filename: string }>
    },
  })

  const ocrMutation = useMutation({
    mutationFn: async (filename: string) => {
      return api<{ task_id: string; status: string }>('/v1/manual/ocr', {
        method: 'POST',
        body: { filename },
      })
    },
  })

  const handleFiles = useCallback((files: FileList | File[]) => {
    const newFiles: UploadedFile[] = Array.from(files).map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))
    setUploadedFiles((prev) => [...prev, ...newFiles])
    // Trigger upload for each file
    Array.from(files).forEach((f) => uploadMutation.mutate(f))
  }, [uploadMutation])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles])

  const handleOcrTrigger = useCallback((filename: string) => {
    setOcrStatus((prev) => ({ ...prev, [filename]: 'processing' }))
    ocrMutation.mutate(filename, {
      onSuccess: () => setOcrStatus((prev) => ({ ...prev, [filename]: 'done' })),
      onError: () => setOcrStatus((prev) => ({ ...prev, [filename]: 'error' })),
    })
  }, [ocrMutation])

  const isImageFile = (type: string) => type.startsWith('image/')

  return (
    <div className="space-y-4" data-testid="document-upload-panel">
      {/* Drag-and-drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          flex flex-col items-center justify-center rounded-lg border-2 border-dashed
          px-6 py-10 text-center cursor-pointer transition-colors
          ${isDragging
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5'
            : 'border-[var(--color-border)] bg-[var(--color-bg-secondary)] hover:border-[var(--color-accent)]/50'
          }
        `}
        data-testid="file-dropzone"
      >
        <svg className="h-10 w-10 text-[var(--color-text-muted)] mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
        </svg>
        <p className="text-sm font-medium text-[var(--color-text-primary)]">
          {isDragging ? 'Drop files here' : 'Drag & drop files here'}
        </p>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          or click to browse. Supports PDF, images, and documents.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.gif,.tiff,.doc,.docx,.txt"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files)
          }}
          data-testid="file-input"
        />
      </div>

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2" data-testid="uploaded-files-list">
          <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Uploaded Files ({uploadedFiles.length})
          </h3>
          {uploadedFiles.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2"
            >
              {/* Thumbnail or icon */}
              {file.previewUrl ? (
                <img src={file.previewUrl} alt={file.name} className="h-10 w-10 rounded object-cover shrink-0" />
              ) : (
                <div className="h-10 w-10 rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center shrink-0">
                  <svg className="h-5 w-5 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{file.name}</p>
                <p className="text-[10px] text-[var(--color-text-muted)]">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              {/* OCR trigger button for images */}
              {isImageFile(file.type) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOcrTrigger(file.name)
                  }}
                  disabled={ocrStatus[file.name] === 'processing'}
                  className="
                    inline-flex items-center gap-1 rounded-md
                    border border-[var(--color-border)]
                    bg-[var(--color-bg-secondary)] px-2 py-1
                    text-[10px] font-medium text-[var(--color-text-secondary)]
                    hover:bg-[var(--color-bg-tertiary)] transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  data-testid="ocr-trigger-btn"
                >
                  {ocrStatus[file.name] === 'processing' ? (
                    <>
                      <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      Processing...
                    </>
                  ) : ocrStatus[file.name] === 'done' ? (
                    <>
                      <svg className="h-3 w-3 text-[var(--color-success)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      OCR Done
                    </>
                  ) : ocrStatus[file.name] === 'error' ? (
                    <>
                      <svg className="h-3 w-3 text-[var(--color-danger)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" />
                      </svg>
                      Retry OCR
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M9 15h6M9 11h6" />
                      </svg>
                      Run OCR
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ───── Page Component ───── */

function ManualPage() {
  const [activeSection, setActiveSection] = useState(MANUAL_SECTIONS[0]!.id)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [zoom, setZoom] = useState(100)

  // Search: filter sections that contain the query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return MANUAL_SECTIONS
    const q = searchQuery.toLowerCase()
    return MANUAL_SECTIONS.filter(
      (s) => s.title.toLowerCase().includes(q) || s.content.toLowerCase().includes(q),
    )
  }, [searchQuery])

  // In paginated mode, show one section per "page"
  const totalPages = filteredSections.length
  const displayedSection = filteredSections[currentPage] ?? filteredSections[0]

  // Sync activeSection with pagination
  const handlePageChange = useCallback(
    (page: number) => {
      const clamped = Math.max(0, Math.min(page, totalPages - 1))
      setCurrentPage(clamped)
      const section = filteredSections[clamped]
      if (section) setActiveSection(section.id)
    },
    [filteredSections, totalPages],
  )

  const handleSectionSelect = useCallback(
    (id: string) => {
      setActiveSection(id)
      const idx = filteredSections.findIndex((s) => s.id === id)
      if (idx >= 0) setCurrentPage(idx)
    },
    [filteredSections],
  )

  // Download manual as text file
  const handleDownload = useCallback(() => {
    const content = MANUAL_SECTIONS.map(
      (s) => `${'='.repeat(60)}\n${s.title.toUpperCase()}\n${'='.repeat(60)}\n\n${s.content}\n`,
    ).join('\n\n')
    const blob = new Blob([`IRIS User Manual\n\n${content}`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'iris-user-manual.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  // Print-friendly mode
  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  // Keyboard navigation: left/right arrow keys for page navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when user is typing in input fields
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePageChange(currentPage - 1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handlePageChange(currentPage + 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentPage, handlePageChange])

  return (
    <div className="flex h-full" data-testid="manual-page">
      {/* Print-optimized styles */}
      <style>{`
        @media print {
          /* Hide non-content elements */
          [data-testid="manual-sidenav"],
          [data-testid="manual-toolbar"],
          [data-testid="manual-search"],
          [data-testid="manual-download"],
          [data-testid="manual-print"],
          nav, aside, header {
            display: none !important;
          }
          /* Reset layout for print */
          [data-testid="manual-page"] {
            display: block !important;
          }
          [data-testid="manual-page"] > main {
            overflow: visible !important;
          }
          [data-testid="manual-page-content"] {
            box-shadow: none !important;
            border: none !important;
            max-width: 100% !important;
            font-size: 12pt !important;
          }
          /* Ensure all sections print, not just current page */
          .print-all-sections {
            display: block !important;
          }
          body {
            background: white !important;
            color: black !important;
          }
          /* Page breaks between sections */
          .print-section {
            page-break-after: always;
          }
          .print-section:last-child {
            page-break-after: auto;
          }
        }
      `}</style>

      {/* Left: Table of Contents */}
      <aside className="w-64 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-surface)] overflow-y-auto print:hidden">
        <div className="sticky top-0 p-4">
          <div className="mb-4">
            <h1 className="text-lg font-bold text-[var(--color-text-heading)]">User Manual</h1>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">IRIS Platform Guide</p>
          </div>

          {/* Search within manual */}
          <div className="mb-3">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(0)
                }}
                placeholder="Search manual..."
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-input,var(--color-surface-secondary))] pl-8 pr-3 py-1.5 text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-[var(--color-accent)]"
                data-testid="manual-search"
              />
            </div>
            {searchQuery && (
              <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} match
              </p>
            )}
          </div>

          {/* Download button */}
          <button
            type="button"
            onClick={handleDownload}
            className="mb-3 w-full flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
            data-testid="manual-download"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Manual
          </button>

          {/* Print button */}
          <button
            type="button"
            onClick={handlePrint}
            className="mb-3 w-full flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
            data-testid="manual-print"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print Manual
          </button>

          <SideNav sections={filteredSections} activeId={activeSection} onSelect={handleSectionSelect} />
        </div>
      </aside>

      {/* Right: PDF-like Content Viewer */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Toolbar: pagination + zoom */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-4 py-2" data-testid="manual-toolbar">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 0}
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30 disabled:pointer-events-none transition-colors"
              aria-label="Previous page"
              data-testid="manual-prev-page"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span className="text-xs text-[var(--color-text-secondary)]" data-testid="manual-page-indicator">
              Page {currentPage + 1} of {totalPages}
            </span>
            <span className="text-[10px] text-[var(--color-text-muted)] hidden sm:inline" data-testid="manual-keyboard-hint">
              (use arrow keys)
            </span>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30 disabled:pointer-events-none transition-colors"
              aria-label="Next page"
              data-testid="manual-next-page"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(50, z - 10))}
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
              aria-label="Zoom out"
              data-testid="manual-zoom-out"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /><path d="M8 11h6" />
              </svg>
            </button>
            <span className="text-xs text-[var(--color-text-secondary)] w-10 text-center" data-testid="manual-zoom-level">
              {zoom}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(200, z + 10))}
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
              aria-label="Zoom in"
              data-testid="manual-zoom-in"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /><path d="M11 8v6M8 11h6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Document Upload Panel */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-4">
          <DocumentUploadPanel />
        </div>

        {/* PDF-like page */}
        <div className="flex-1 overflow-y-auto bg-[var(--color-surface-secondary)] p-6">
          {displayedSection ? (
            <div
              className="mx-auto bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-sm"
              style={{ maxWidth: `${(640 * zoom) / 100}px`, fontSize: `${zoom}%` }}
              data-testid="manual-page-content"
            >
              <div className="px-10 py-8">
                <h2 className="text-2xl font-bold text-[var(--color-text-heading)] mb-6">
                  <HighlightText text={displayedSection.title} query={searchQuery} />
                </h2>
                <SectionContent content={displayedSection.content} searchQuery={searchQuery} />
              </div>
              {/* Page footer */}
              <div className="border-t border-[var(--color-border)] px-10 py-3 flex items-center justify-between text-[10px] text-[var(--color-text-muted)]">
                <span>IRIS User Manual</span>
                <span>{currentPage + 1}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-sm text-[var(--color-text-secondary)]">
              No matching sections found.
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
